import {
    ASSIGN_QUESTIONS_TO_FORM_SECTION,
    CREATE_QUESTIONS_URLS,
} from "@/utils/constants/constants";
import axios from "axios";

export default {
    methods: {
        create_empty_option() {
            return { choice: "", is_mandatory: false };
        },

        normalize_option_input(option) {
            if (option && typeof option === "object" && !Array.isArray(option)) {
                return {
                    choice: String(option.choice || ""),
                    is_mandatory: Boolean(option.is_mandatory),
                };
            }

            return {
                choice: String(option || ""),
                is_mandatory: false,
            };
        },

        get_valid_option_inputs(options = []) {
            return (options || [])
                .map((option) => this.normalize_option_input(option))
                .filter((option) => option.choice.trim() !== "");
        },

        validate_select_mandatory_options(question_type_id, options, label) {
            const selected_type = this.question_types.find(
                (type) => type.id === question_type_id
            );

            if (selected_type?.question_type !== "single_select") {
                return true;
            }

            const mandatory_count = options.filter((option) => option.is_mandatory).length;
            if (mandatory_count > 1) {
                this.show_error(`${label}: only one mandatory option is allowed for single select questions`);
                return false;
            }

            return true;
        },

        add_question_form() {
            this.question_forms.push({
                id: this.question_forms.length + 1,
                question: "",
                question_number: "",
                question_type: "",
                is_mandatory: "",
                show_option_inputs: false,
                option_inputs: [this.create_empty_option(), this.create_empty_option()],
                data_point_id: null,
                available_tags: [],
                selected_tag: null,
                dependant_questions: [],
            });
        },

        remove_question_form(index) {
            if (this.question_forms.length > 1) {
                this.question_forms.splice(index, 1);
            } else {
                this.show_error("At least one question form is required");
            }
        },

        reset_question_form() {
            this.question_forms = [{
                id: 1,
                question: "",
                question_number: "",
                question_type: "",
                is_mandatory: "",
                show_option_inputs: false,
                option_inputs: [this.create_empty_option(), this.create_empty_option()],
                data_point_id: null,
                available_tags: [],
                selected_tag: null,
                dependant_questions: [],
            }];
        },

        open_create_questions_modal() {
            this.reset_question_form();
            this.show_create_modal = true;
        },

        close_create_modal() {
            this.show_create_modal = false;
        },

        add_option_input(form) {
            form.option_inputs.push(this.create_empty_option());
        },

        remove_option_input(form, index) {
            if (form.option_inputs.length > 2) {
                form.option_inputs.splice(index, 1);
            }
        },

        handle_question_type_change_for_form(form) {
            if (!form.question_type) {
                form.show_option_inputs = false;
                return;
            }
            const selected_type = this.question_types.find(
                (type) => type.id === form.question_type
            );
            form.show_option_inputs = this.select_types.includes(
                selected_type?.question_type
            );
            if (form.show_option_inputs) {
                if (form.option_inputs.length < 2) {
                    form.option_inputs = [this.create_empty_option(), this.create_empty_option()];
                } else {
                    form.option_inputs = form.option_inputs.map((option) => this.normalize_option_input(option));
                }
            } else {
                form.option_inputs = [];
            }
            form.selected_tag = null;
        },

        handle_question_type_change_for_edit(question) {
            if (!question.question_type) {
                question.show_option_inputs = false;
                return;
            }
            const selected_type = this.question_types.find(
                (type) => type.id === question.question_type
            );
            question.show_option_inputs = this.select_types.includes(
                selected_type?.question_type
            );
            if (question.show_option_inputs) {
                if (!question.option_inputs || question.option_inputs.length < 2) {
                    question.option_inputs = [this.create_empty_option(), this.create_empty_option()];
                } else {
                    question.option_inputs = question.option_inputs.map((option) => this.normalize_option_input(option));
                }
            } else {
                question.option_inputs = [];
            }
        },

        get_question_type_hint(type_id) {
            if (!type_id) return '';
            const selected = this.question_types.find(t => t.id === type_id);
            if (!selected) return '';
            const hints = {
                sa_phone_number: 'Validates SA mobile numbers (06x/07x/08x, 10 digits)',
                telephone: 'Validates SA telephone numbers (01x–08x, 10 digits)',
                fax: 'Validates SA fax numbers (10 digits)',
                email: 'Validates email address format',
                postal_code: 'Validates 4-digit SA postal codes',
                passport_number: 'Validates passport numbers (6–9 alphanumeric characters)',
                pdf_file: 'Only PDF file uploads are accepted (max 5MB)',
                file: 'Only PDF file uploads are accepted (max 5MB)',
            };
            return hints[selected.question_type] || '';
        },

        async save_all_questions() {
            for (const [index, form] of this.question_forms.entries()) {
                if (!form.question?.trim()) {
                    this.show_error(`Question text cannot be empty for form ${index + 1}`);
                    return;
                }
                if (!form.question_type) {
                    this.show_error(`Question type cannot be empty for form ${index + 1}`);
                    return;
                }

                if (form.show_option_inputs) {
                    const valid_options = this.get_valid_option_inputs(form.option_inputs);
                    if (valid_options.length < 2) {
                        this.show_error(`At least two valid options are required for form ${index + 1}`);
                        return;
                    }
                    if (!this.validate_select_mandatory_options(form.question_type, valid_options, `Question ${index + 1}`)) {
                        return;
                    }
                }

                for (const [dep_index, dep_question] of (form.dependant_questions || []).entries()) {
                    if (!dep_question.question_text?.trim()) {
                        this.show_error(`Dependent question text cannot be empty for dependent question ${dep_index + 1} in form ${index + 1}`);
                        return;
                    }
                    if (!dep_question.question_type_id) {
                        this.show_error(`Dependent question type cannot be empty for dependent question ${dep_index + 1} in form ${index + 1}`);
                        return;
                    }
                    if (!dep_question.trigger_condition?.trim()) {
                        this.show_error(`Trigger condition cannot be empty for dependent question ${dep_index + 1} in form ${index + 1}`);
                        return;
                    }

                    if (dep_question.show_option_inputs) {
                        const valid_dep_options = dep_question.options.filter((opt) => opt.choice?.trim());
                        if (valid_dep_options.length < 2) {
                            this.show_error(`At least two valid options are required for dependent question ${dep_index + 1} in form ${index + 1}`);
                            return;
                        }
                    }
                }
            }

            for (const form of this.question_forms) {
                const multi_select_options = form.show_option_inputs
                    ? this.get_valid_option_inputs(form.option_inputs).map((opt) => ({
                        choice: opt.choice.trim(),
                        is_mandatory: opt.is_mandatory,
                    }))
                    : [];

                const dependant_questions = (form.dependant_questions || []).map((dep) => {
                    let dep_multi_select_options = [];
                    if (dep.show_option_inputs && dep.options && dep.options.length > 0) {
                        dep_multi_select_options = dep.options
                            .filter((opt) => opt.choice?.trim())
                            .map((opt) => opt.choice.trim());
                    }

                    return {
                        question_text: dep.question_text.trim(),
                        question_type_id: dep.question_type_id,
                        data_point_id: dep.data_point_id || null,
                        tag_id: this.validate_tag_id(dep.tag_id),
                        question_number: dep.question_number || null,
                        trigger_condition: dep.trigger_condition.trim(),
                        is_mandatory: dep.is_mandatory,
                        multi_select_options: dep_multi_select_options
                    };
                });

                const payload = {
                    question_text: form.question.trim(),
                    question_type_id: form.question_type,
                    project_id: this.project_id,
                    is_mandatory: form.is_mandatory === "Yes",
                    question_number: form.question_number || null,
                    multi_select_options: multi_select_options,
                    section_id: this.section_id,
                    data_point_id: form.data_point_id || null,
                    tag_id: this.validate_tag_id(form.selected_tag),
                    dependant_questions: dependant_questions,
                };

                const response = await axios.post(CREATE_QUESTIONS_URLS, payload, {
                    headers: this.headers,
                }).catch((error) => {
                    let error_msg = "Error creating questions";
                    if (error.response) {
                        if (error.response.data?.message) {
                            if (typeof error.response.data.message === "string") {
                                error_msg = error.response.data.message;
                            } else if (typeof error.response.data.message === "object") {
                                error_msg = Object.entries(error.response.data.message)
                                    .map(([field, errors]) => {
                                        const error_list = Array.isArray(errors)
                                            ? errors.join(" ")
                                            : errors;
                                        return `${field}: ${error_list}`;
                                    })
                                    .join("\n");
                            }
                        }
                    } else {
                        error_msg = error.message;
                    }
                    this.show_error(error_msg);
                    return null;
                });

                const response_data = typeof response?.data === "string"
                    ? JSON.parse(response.data)
                    : response?.data;

                const question_id = response_data?.data?.question_id;

                if (question_id && this.section_id) {
                    await axios.post(
                        ASSIGN_QUESTIONS_TO_FORM_SECTION,
                        {
                            project_id: this.project_id,
                            form_id: this.form_id,
                            section_id: this.section_id,
                            question_ids: [question_id],
                            is_mandatory: payload.is_mandatory,
                        },
                        { headers: this.headers }
                    ).catch((error) => {
                        const message = error.response?.data?.message || error.message;
                        this.show_error(`Error assigning question: ${message}`);
                    });
                }
            }

            this.show_success("All questions created successfully");
            this.reset_question_form();
            this.close_create_modal();
            window.location.reload();
        },

        validate_tag_id(tag_id) {
            if (tag_id === null || tag_id === undefined || tag_id === "") {
                return null;
            }
            return parseInt(tag_id, 10);
        },

        add_dependent_question(form) {
            form.dependant_questions.push({
                question_text: "",
                question_type_id: "",
                data_point_id: null,
                tag_id: null,
                available_tags: [],
                question_number: "",
                trigger_condition: "",
                is_mandatory: false,
                show_option_inputs: false,
                options: [{ choice: "" }, { choice: "" }],
                parent_options: form.show_option_inputs
                    ? this.get_valid_option_inputs(form.option_inputs).map((opt) => opt.choice)
                    : []
            });
        },

        remove_dependent_question(form, index) {
            form.dependant_questions.splice(index, 1);
        },

        handle_dependent_question_type_change(dep_question) {
            if (!dep_question.question_type_id) {
                dep_question.show_option_inputs = false;
                return;
            }
            const selected_type = this.question_types.find(
                (type) => type.id === dep_question.question_type_id
            );
            dep_question.show_option_inputs = this.select_types.includes(
                selected_type?.question_type
            );
            if (dep_question.show_option_inputs) {
                if (!dep_question.options || dep_question.options.length < 2) {
                    dep_question.options = [{ choice: "" }, { choice: "" }];
                }
            } else {
                dep_question.options = [];
            }
        },

        add_dependent_option(dep_question) {
            dep_question.options.push({ choice: "" });
        },

        remove_dependent_option(dep_question, index) {
            if (dep_question.options.length > 2) {
                dep_question.options.splice(index, 1);
            }
        },

        load_tags_for_data_point(form) {
            form.available_tags = this.get_tags_for_data_point(form.data_point_id);
            form.selected_tag = null;
        },

        get_tags_for_data_point(data_point_id) {
            if (!data_point_id) return [];
            const data_point = this.data_points.find(dp => dp.id === data_point_id);
            return data_point?.tags || [];
        },

        should_disable_tag_selection(form) {
            return !form.data_point_id || !form.available_tags || form.available_tags.length === 0;
        }
    }
};